import React, {
    ReactElement,
    useState,
} from "react";
import { Form } from "react-bootstrap";
import { BiCopy, BiPencil, BiTrash } from "react-icons/bi";
import Confirm from "../../../../components/confirm/Confirm";
import { Json, createId } from "./base";
import Template, { MapParameters, NamedParameters } from "./Template";

import Select from "../../../../components/select/Select";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import SerializationSelect from "./Serialization";
import Function, { FunctionObject } from "./Function";
import SortableList from "../../../../components/sortable-list/SortableList";
import WorkflowServiceApiSelector from "./ApiSelector";

enum AuthenticationTypes {
    None = "NONE",
    RefreshToken = "REFRESH_TOKEN",
    AccessToken = "ACCESS_TOKEN",
    CustomToken = "CUSTOM_TOKEN",
}

export default function WorkflowServiceApis({ json, onChange, rootJson }: {
    rootJson: Json,
    json: Json[];
    onChange: (current: Json[]) => void;
}): ReactElement {
    const [editing, setEditing] = useState<{index: number; api: Json}>();
    const handleChange = (index: number, value: Json) => {
        const copy = [...(json ?? [])];
        copy[index] = { ...copy[index], ...value };
        onChange(copy);
    };

    const [editBodyParametersState, setEditBodyParametersState] = useState<Json[] | undefined>();
    const [editBodyRawState, setEditBodyRawState] = useState<Json | undefined>();

    const [editOutputState, setEditOutputState] = useState<Json | undefined>();
    const [editStep, setEditStep] = useState<{index: number; step: Json} | undefined>();

    const handleDeleteApi = (index: number) => {
        const copy = [...(json ?? [])];
        copy.splice(index, 1);
        onChange(copy);
    };

    const handleDuplicate = (index: number) => {
        const copy = [...(json ?? [])];
        copy.push(copy[index]);
        onChange(copy);
    };

    const editApi = (api: Json) => {
        setEditing({
            index: editing?.index as number,
            api: { ...editing?.api, ...api },
        });
    };

    const selectedAuth = Object.entries(AuthenticationTypes).find(([, value]) => value === editing?.api?.authentication);
    const editingApi = (editing?.api as Json);

    const apis = (rootJson.apis ?? []) as Json[];

    const steps = (editingApi?.steps as Json)?.steps as Json[] ?? [];

    return (
        <div>

            {editStep && (
                <Confirm
                    shouldShow
                    onHide={() => setEditStep(undefined)}
                    size="xl"
                    confirmText="Save"
                    onConfirm={() => {
                        const copy = [...steps];
                        copy[editStep.index] = editStep.step;
                        editApi({ steps: { steps: copy } });
                        setEditStep(undefined);
                    }}
                >
                    <WorkflowServiceApiSelector
                        rootJson={rootJson}
                        json={editStep.step?.api as Json}
                        onChange={value => {
                            setEditStep({
                                index: editStep.index,
                                step: {
                                    api: value,
                                },
                            });
                        }}
                    />
                </Confirm>
            )}
            {editBodyParametersState && (
                <Confirm
                    shouldShow
                    onHide={() => setEditBodyParametersState(undefined)}
                    size="xl"
                    confirmText="Save"
                    onConfirm={() => {
                        editApi({ bodyParameters: editBodyParametersState });
                        setEditBodyParametersState(undefined);
                    }}
                >
                    <FunctionObject
                        title="Body Parameters"
                        json={{ values: editBodyParametersState }}
                        onChange={({ values }) => {
                            // st
                            setEditBodyParametersState(values as Json[]);
                        }}
                    />
                </Confirm>
            )}
            {editBodyRawState && (
                <Confirm
                    shouldShow
                    onHide={() => setEditBodyRawState(undefined)}
                    size="xl"
                    confirmText="Save"
                    onConfirm={() => {
                        editApi({ bodyRaw: editBodyRawState });
                        setEditBodyRawState(undefined);
                    }}
                >
                    <Function
                        title="Body"
                        json={editBodyRawState}
                        onChange={value => {
                            setEditBodyRawState(value);
                        }}
                    />
                </Confirm>
            )}
            {editOutputState && (
                <Confirm
                    shouldShow
                    onHide={() => setEditOutputState(undefined)}
                    size="xl"
                    confirmText="Save"
                    onConfirm={() => {
                        editApi({ output: { function: editOutputState } });
                        setEditOutputState(undefined);
                    }}
                >
                    <Function
                        title="Output"
                        json={editOutputState}
                        onChange={value => {
                            // st
                            setEditOutputState(value);
                        }}
                    />
                </Confirm>
            )}
            {editing && (
                <Confirm
                    shouldShow
                    onHide={() => setEditing(undefined)}
                    size="xl"
                    confirmText="Save"
                    onConfirm={() => {
                        handleChange(editing.index, editing.api);
                        setEditing(undefined);
                    }}
                >
                    <div className="row">
                        <div className="col-4">
                            <ExpandingSection title="Basic Info">
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Id</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Api Id"
                                        required
                                        onChange={evt => editApi({ id: evt.target.value })}
                                        value={editing.api?.id as string ?? ""}
                                    />
                                    <Form.Text>
                                        Id of the action
                                    </Form.Text>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-none btn-sm"
                                            onClick={() => editApi({
                                                id: `urn:fq:workflow-service-api:(`
                                            + `urn:fq:workflow-service:${rootJson.id},${createId()})`,
                                            })}
                                        >
                                            Generate Id
                                        </button>
                                    </div>
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="My Action"
                                        required
                                        onChange={evt => editApi({ name: evt.target.value })}
                                        value={editing.api?.name as string ?? ""}
                                    />
                                    <Form.Text>
                                        Name of the action
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Description</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Description"
                                        required
                                        as="textarea"
                                        onChange={evt => editApi({ description: evt.target.value })}
                                        value={editing.api?.description as string ?? ""}
                                    />
                                    <Form.Text>
                                        Description
                                    </Form.Text>
                                </Form.Group>
                            </ExpandingSection>
                            <ExpandingSection title="Request">
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Url</Form.Label>
                                    <Template
                                        json={editing.api.url as Json}
                                        onChange={value => editApi({ url: value?.template ? value : undefined })}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Method</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="GET"
                                        onChange={evt => editApi({ method: evt.target.value })}
                                        value={editing.api?.method as string ?? ""}
                                    />
                                    <Form.Text>
                                        The request method - GET, POST, PUT
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Authentication</Form.Label>
                                    <Select
                                        onChange={value => editApi({
                                            authentication: value.value,
                                        })}
                                        value={!selectedAuth ? undefined : {
                                            label: selectedAuth[0] as string,
                                            value: selectedAuth[1] as string,
                                        }}
                                        options={Object.entries(AuthenticationTypes).map(([key, label]) => ({
                                            label: key as string,
                                            value: label as string,
                                        }))}
                                    />
                                    <Form.Text>
                                        The type of authentication the api users
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder">Serialization</Form.Label>

                                    <SerializationSelect
                                        value={(editing.api as Json)?.serialization as string}
                                        onChange={serialization => editApi({
                                            serialization: serialization as string,
                                        })}
                                    />
                                    <Form.Text>
                                        Serialization type
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Timeout (ms)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="1000"
                                        onChange={evt => editApi({ timeout: parseInt(evt.target.value, 10) })}
                                        value={editing.api?.timeout as string ?? ""}
                                    />
                                    <Form.Text>
                                        The timeout in milliseconds
                                    </Form.Text>
                                </Form.Group>
                            </ExpandingSection>
                            <ExpandingSection title="Query Parameters">
                                <NamedParameters
                                    json={(editing?.api as Json)?.queryParameters as Json[] ?? []}
                                    onChange={queryParameters => editApi({ queryParameters })}
                                />
                            </ExpandingSection>
                            <ExpandingSection title="Headers">
                                <MapParameters
                                    json={(editing?.api as Json)?.headers as Json}
                                    onChange={headers => editApi({ headers })}
                                />
                            </ExpandingSection>
                            <ExpandingSection title="Body Parameters">
                                <button
                                    type="button"
                                    className="btn btn-primary me-2 mb-2"
                                    onClick={() => {
                                        setEditBodyParametersState((editing?.api as Json)?.bodyParameters as Json[] ?? []);
                                    }}
                                >
                                    Edit Parameters
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary me-2 mb-2"
                                    onClick={() => {
                                        setEditBodyRawState((editing?.api as Json)?.bodyRaw as Json ?? {});
                                    }}
                                >
                                    Edit Raw Body
                                </button>
                                {((editing?.api as Json)?.bodyParameters as Json || (editing?.api as Json)?.bodyRaw as Json) && (
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => {
                                            editApi({ bodyParameters: undefined });
                                        }}
                                    >
                                        Reset
                                    </button>
                                )}

                            </ExpandingSection>
                            <ExpandingSection title="Steps">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setEditStep({
                                            step: {},
                                            index: ((editingApi.steps as Json)?.steps as Json[])?.length ?? 0,
                                        });
                                    }}
                                >
                                    Add
                                </button>

                                <SortableList
                                    getId={row => row.id as string}
                                    items={steps}
                                    onReorder={(values: Json[]) => {
                                        editApi({ steps: { steps: values } });
                                    }}
                                    listClassName="list-group list-group-flush mt-2"
                                    itemClassName="list-group-item d-flex justify-content-between"
                                    render={(step, stepIndex) => {
                                        const api = apis.find(current => current.id === (step.api as Json)?.apiId);
                                        return (
                                            <div className="d-flex align-items-center justify-content-between">

                                                <h5 className="mb-0">
                                                    {api?.name as string}
                                                </h5>

                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-none"
                                                        onClick={() => {
                                                            setEditStep({
                                                                index: stepIndex,
                                                                step: steps[stepIndex],
                                                            });
                                                        }}
                                                    >
                                                        <BiPencil className="text-light" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-none"
                                                        onClick={() => {
                                                            const copy = [...steps];
                                                            copy.splice(stepIndex, 1);
                                                            editApi({ steps: { steps: copy } });
                                                        }}
                                                    >
                                                        <BiTrash className="text-light" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-none"
                                                        onClick={() => {
                                                            const copy = [...steps];
                                                            copy.push({
                                                                ...copy[stepIndex],
                                                            });
                                                            editApi({ steps: { steps: copy } });
                                                        }}
                                                    >
                                                        <BiCopy className="text-light" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }}
                                />

                            </ExpandingSection>
                            <ExpandingSection title="Output">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setEditOutputState(((editing?.api as Json)?.output as Json)?.function as Json ?? {});
                                    }}
                                >
                                    Edit
                                </button>
                                {(editing?.api as Json)?.output as Json && (
                                    <button
                                        type="button"
                                        className="btn btn-danger ms-2"
                                        onClick={() => {
                                            editApi({ output: undefined });
                                        }}
                                    >
                                        Reset
                                    </button>
                                )}

                            </ExpandingSection>

                        </div>

                    </div>
                </Confirm>
            )}

            <SortableList
                getId={row => row.id as string}
                items={json ?? []}
                onReorder={(list: Json[]) => onChange(list)}
                listClassName="list-group list-group-flush mt-2"
                itemClassName="list-group-item d-flex justify-content-between"
                render={(api, index) => (
                    <>
                        <div>
                            <strong>
                                {api?.name as string}
                            </strong>
                            <div className="text-start">
                                {api?.description as string}
                            </div>
                        </div>
                        <div>
                            <button
                                type="button"
                                className="btn btn-none"
                                onClick={() => setEditing({ index, api })}
                            >
                                <BiPencil className="text-light" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-none"
                                onClick={() => handleDeleteApi(index)}
                            >
                                <BiTrash className="text-light" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-none"
                                onClick={() => handleDuplicate(index)}
                            >
                                <BiCopy className="text-light" />
                            </button>
                        </div>

                    </>
                )}
            />

            <button
                type="button"
                className="btn btn-primary"
                onClick={() => setEditing({ index: (json ?? []).length, api: {} })}
            >
                Add Api
            </button>

        </div>
    );
}
